import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'postcode', 'list', 'listContainer',
    'line1', 'line2', 'line3', 'line4',
    'city', 'county', 'country',
    'postcodeError', 'searchError', 'noResults',
    'manualFields'
  ]
  static values = { url: String }

  revealManual(event) {
    event?.preventDefault()
    this.#revealManualFields()
  }

  async search(event) {
    event?.preventDefault()

    this.#hide(this.postcodeErrorTarget)
    if (this.hasSearchErrorTarget) this.#hide(this.searchErrorTarget)
    if (this.hasNoResultsTarget) this.#hide(this.noResultsTarget)
    if (this.hasListContainerTarget) this.#hide(this.listContainerTarget)

    const postcode = this.postcodeTarget.value.trim().toUpperCase()
    if (!postcode) {
      this.#show(this.postcodeErrorTarget)
      return
    }
    this.postcodeTarget.value = postcode

    const params = new URLSearchParams({ postcode, expand: 'true' })

    try {
      const response = await fetch(`${this.urlValue}?${params}`)
      const data = await response.json()
      this.#renderResults(data)
    } catch (error) {
      console.error('Address lookup error:', error)
      if (this.hasSearchErrorTarget) this.#show(this.searchErrorTarget)
      this.#revealManualFields()
    }
  }

  select(event) {
    const raw = event.target.value
    if (!raw) return
    try {
      this.#applyAddress(JSON.parse(decodeURIComponent(raw)))
    } catch (error) {
      console.error('Address selection error:', error)
    }
  }

  #renderResults(data) {
    if (data.error) {
      if (this.hasSearchErrorTarget) this.#show(this.searchErrorTarget)
      this.#revealManualFields()
      return
    }
    const addresses = Array.isArray(data.addresses) ? data.addresses : []
    if (addresses.length === 0) {
      if (this.hasNoResultsTarget) this.#show(this.noResultsTarget)
      this.#revealManualFields()
      return
    }
    this.#populateList(addresses)
    if (this.hasListContainerTarget) this.#show(this.listContainerTarget)
    if (addresses.length === 1) this.#selectInList(addresses[0])
  }

  #populateList(addresses) {
    const controller = this.#listController()
    if (!controller) return
    controller.refreshOptions(addresses.map((address) => ({
      id: encodeURIComponent(JSON.stringify(address)),
      name: this.#formatAddress(address),
    })))
  }

  #selectInList(address) {
    const controller = this.#listController()
    if (!controller) return
    controller.selectItem(encodeURIComponent(JSON.stringify(address)))
  }

  #formatAddress(address) {
    return (address.formatted_address || [])
      .filter((part) => typeof part === 'string' && part.trim() !== '')
      .join(', ')
  }

  #listController() {
    const wrapper = this.listTarget.querySelector('[data-controller~="single-select-field-component-with-search"]')
    return wrapper && this.application.getControllerForElementAndIdentifier(
      wrapper,
      'single-select-field-component-with-search'
    )
  }

  #applyAddress(address) {
    this.#revealManualFields()
    if (this.hasLine1Target) this.line1Target.value = address.line_1 || ''
    if (this.hasLine2Target) this.line2Target.value = address.line_2 || ''
    if (this.hasLine3Target) this.line3Target.value = address.line_3 || ''
    if (this.hasLine4Target) this.line4Target.value = address.line_4 || ''
    if (this.hasCityTarget)   this.cityTarget.value   = address.town_or_city || ''
    if (this.hasCountyTarget) this.countyTarget.value = address.county || ''
    if (this.hasCountryTarget) this.#setCountry(address.country)
  }

  #setCountry(countryName) {
    if (!countryName) return
    const lookup = JSON.parse(this.countryTarget.dataset.countries || '{}')
    const value = lookup[countryName]
    if (!value) return

    const wrapper = this.countryTarget.closest('[data-controller~="single-select-field"]')
    const controller = wrapper && this.application.getControllerForElementAndIdentifier(
      wrapper,
      'single-select-field'
    )
    if (controller) controller.setValue(value)
  }

  #revealManualFields() {
    if (this.hasManualFieldsTarget) this.#show(this.manualFieldsTarget)
  }

  #show(el) { el.classList.remove('hidden') }
  #hide(el) { el.classList.add('hidden') }
}
